/*
  "Mais para a moto" (sections/mais-para-moto.liquid): outras peças em estoque para a mesma moto, uma linha por
  departamento (o do produto primeiro), alternando as categorias. Quando a seção chega perto da tela:
  1. escolhe as motos: a do cliente (a última escolhida na barra "Minha moto", assets/moto-cliente.js), se o produto
     serve nela; senão, todas as motos do produto, misturadas (dono 30/09);
  2. conta as peças por tipo na coleção da marca da moto, com o filtro das motos (sections/mais-para-moto-tipos.liquid,
     uma página por 250 produtos): só entram departamentos com peça, com o total de cada um;
  3. traz os cards de todos os departamentos ao mesmo tempo (sections/mais-para-moto-itens.liquid, mais vendidos
     primeiro), tira o produto da página e os que já estão nos semelhantes e monta uma linha por departamento com o
     <template data-grupo>: até 4 cards, um de cada categoria por vez (a CSS mostra 2 no celular e 3 no tablet).
  Produto sem moto: de /collections/all. Capacete e roupa: os departamentos da lista "Categorias sem moto"; peça de moto
  sem moto cadastrada (óleo, fluido): só o departamento dela.
*/
if (!customElements.get('mais-para-moto')) {
  const FILTRO_MOTOS = 'filter.p.m.custom.motos';
  const FILTRO_TIPO = 'filter.p.product_type';
  const GID = 'gid://shopify/Metaobject/';
  const MAX_PAGINAS = 20;
  const POR_LINHA = 4;
  const MAX_MOTOS = 100;
  // Mesmo slug de assets/minha-moto.js (cli.slug): a coleção da marca é "moto-<slug>".
  const handleize = (texto) =>
    texto
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  // "# Departamento" e "Nome | Tipo de produto" (formato da barra de categorias) -> [{nome, categorias: [{nome, tipo}]}]
  const lerCategorias = (texto) => {
    const departamentos = [];
    (texto || '').split(/\r?\n/).forEach((linha) => {
      const t = linha.trim();
      if (!t || t.startsWith('@')) return;
      if (t.startsWith('#')) {
        departamentos.push({ nome: t.slice(1).trim(), categorias: [] });
        return;
      }
      const [nome, tipo] = t.split('|').map((parte) => parte.trim());
      if (!nome || !tipo) return;
      if (!departamentos.length) departamentos.push({ nome: '', categorias: [] });
      departamentos[departamentos.length - 1].categorias.push({ nome, tipo });
    });
    return departamentos.filter((departamento) => departamento.categorias.length);
  };

  // "Capacetes", "Capacetes e Roupas", "A, B e C"
  const juntarNomes = (nomes) => (nomes.length < 2 ? nomes.join('') : `${nomes.slice(0, -1).join(', ')} e ${nomes.at(-1)}`);

  const baixar = async (url) => {
    const resposta = await fetch(url);
    if (!resposta.ok) throw new Error(`HTTP ${resposta.status}`);
    return new DOMParser().parseFromString(await resposta.text(), 'text/html');
  };

  customElements.define(
    'mais-para-moto',
    class MaisParaMoto extends HTMLElement {
      connectedCallback() {
        if (this.iniciado) return;
        this.iniciado = true;
        this.config = JSON.parse(this.querySelector('script[data-config]').textContent);
        this.caixa = this.querySelector('[data-caixa]');
        this.grupoSeq = 0;
        this.observador = new IntersectionObserver(
          (entradas) => {
            if (!entradas.some((entrada) => entrada.isIntersecting)) return;
            this.observador.disconnect();
            this.iniciar().catch((erro) => {
              console.error(erro);
              this.caixa.hidden = true;
            });
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        this.observador.observe(this);
      }

      disconnectedCallback() {
        this.observador?.disconnect();
      }

      async iniciar() {
        const config = this.config;
        // Motos do produto (snippets/motos-produto.liquid): todos os ids e o nome das 50 primeiras.
        const motos = new Map(
          config.motos.motos.map(([id, marca, modelo, ano]) => [String(id), { id: String(id), marca, modelo, ano }])
        );
        const comMoto = lerCategorias(config.categorias);
        const semMoto = lerCategorias(config.categoriasSemMoto);
        const doTipo = (departamentos) =>
          departamentos.find((departamento) => departamento.categorias.some((categoria) => categoria.tipo === config.tipo));

        let titulo;
        let departamentos;
        let primeiro;
        let escolha;
        if (config.motos.ids.length) {
          escolha = this.escolherMoto(motos);
          this.base = this.urlDasMotos(escolha.colecao, escolha.ids, escolha.misturada);
          titulo = `${config.tituloMoto} ${escolha.rotulo}`;
          departamentos = comMoto;
          primeiro = doTipo(comMoto);
        } else {
          primeiro = doTipo(comMoto) || doTipo(semMoto);
          if (!primeiro) return;
          departamentos = doTipo(comMoto) ? [primeiro] : semMoto;
          this.base = new URL(config.todas, location.origin);
          departamentos
            .flatMap((departamento) => departamento.categorias)
            .forEach((categoria) => this.base.searchParams.append(FILTRO_TIPO, categoria.tipo));
          titulo = `${config.tituloSemMoto} ${juntarNomes(departamentos.map((departamento) => departamento.nome))}`;
        }

        let contagem = await this.contar();
        if (this.filtroIgnorado && escolha?.misturada) {
          // Motos de mais de uma marca vão para /collections/all; acima de 5.000 produtos a loja ignora o filtro: fica
          // a marca da moto do produto.
          const principal = escolha.principal;
          const daMarca = [...motos.values()].filter((moto) => moto.marca === principal.marca).map((moto) => GID + moto.id);
          // (só as motos com nome, as 50 primeiras: das outras não se sabe a marca)
          this.base = this.urlDasMotos(`${config.colecoes}/moto-${handleize(principal.marca)}`, daMarca, true);
          contagem = await this.contar();
        }
        if (this.filtroIgnorado) return;
        this.departamentos = [primeiro, ...departamentos.filter((departamento) => departamento !== primeiro)]
          .filter(Boolean)
          .map((departamento) => {
            // Na linha do departamento do produto, a categoria dele abre a alternância (óleo: outros óleos primeiro).
            const categorias = [...departamento.categorias]
              .sort((a, b) => (b.tipo === config.tipo) - (a.tipo === config.tipo))
              .filter((categoria) => contagem.get(categoria.tipo) > 0)
              .map((categoria) => ({ ...categoria, total: contagem.get(categoria.tipo) }));
            return { ...departamento, categorias, total: categorias.reduce((soma, categoria) => soma + categoria.total, 0) };
          })
          .filter((departamento) => departamento.categorias.length);
        if (!this.departamentos.length) return;

        this.querySelector('[data-titulo]').textContent = titulo;
        const todas = this.querySelector('[data-todas]');
        todas.href = this.base.pathname + this.base.search;
        todas.hidden = false;
        this.caixa.hidden = false;

        const status = this.querySelector('[data-status]');
        const aviso = setTimeout(() => (status.textContent = 'Carregando peças…'), 400);
        try {
          const itens = await Promise.all(
            this.departamentos.map((departamento) =>
              this.itens(departamento).catch((erro) => {
                console.error(erro);
                return [];
              })
            )
          );
          const fora = this.naPagina();
          const grupos = this.departamentos
            .map((departamento, indice) => this.montarLinha(departamento, itens[indice], fora))
            .filter(Boolean);
          this.querySelector('[data-grupos]').replaceChildren(...grupos);
          status.textContent = '';
          if (!grupos.length) this.caixa.hidden = true;
        } finally {
          clearTimeout(aviso);
        }
      }

      // A moto do cliente (escolhida na barra) quando o produto serve nela: "Fazer 250" ou "Fazer 250 2020". Sem ela,
      // todas as motos do produto, misturadas (dono 30/09): "Crosser 150, Fazer 250 e outras 2 motos", com a moto que o
      // título cita primeiro na frente (a mesma da barra). -> {ids, colecao, rotulo, misturada, principal}
      escolherMoto(motos) {
        const config = this.config;
        const cliente = window.MotoCliente?.noProduto(config.motos);
        if (cliente) {
          const nome = cliente.modelo ? [cliente.modelo, cliente.ano].filter(Boolean).join(' ') : 'sua moto';
          return { ids: cliente.ids, colecao: cliente.colecao, rotulo: nome };
        }
        const principal = motos.get(String(config.moto)) || motos.values().next().value;
        const nomeados = [principal, ...[...motos.values()].filter((moto) => moto !== principal)];
        const modelos = [...new Set(nomeados.map((moto) => moto.modelo))];
        const marcas = new Set(nomeados.map((moto) => moto.marca));
        // Mais de 50 motos: o nome das outras não chega ao Liquid (snippets/motos-produto.liquid).
        const semNome = config.motos.ids.length > nomeados.length;
        let rotulo = juntarNomes(modelos);
        if (semNome) rotulo = `${modelos.slice(0, 2).join(', ')} e outras motos`;
        else if (modelos.length > 3) rotulo = `${modelos.slice(0, 2).join(', ')} e outras ${modelos.length - 2} motos`;
        // Até MAX_MOTOS valores no filtro (a URL tem limite), espalhados pela lista inteira: peça universal (258 motos,
        // agrupadas por modelo) mistura todos os modelos, não só os primeiros.
        const todos = config.motos.ids.map(String);
        const passo = Math.max(1, todos.length / MAX_MOTOS);
        const escolhidas = [...new Set(Array.from({ length: Math.min(todos.length, MAX_MOTOS) }, (_, i) => todos[Math.floor(i * passo)]))];
        return {
          ids: escolhidas.map((id) => GID + id),
          colecao: marcas.size === 1 && !semNome ? `${config.colecoes}/moto-${handleize(principal.marca)}` : config.todas,
          rotulo,
          misturada: true,
          principal,
        };
      }

      // URL da coleção com o filtro das motos. Misturada (não é a moto do cliente): "origem=mais-para", para a barra da
      // página de resultado não guardar essas motos como a do cliente (assets/minha-moto.js).
      urlDasMotos(colecao, ids, misturada) {
        const url = new URL(colecao, location.origin);
        ids.forEach((id) => url.searchParams.append(FILTRO_MOTOS, id));
        if (misturada) url.searchParams.set('origem', 'mais-para');
        return url;
      }

      // Peças por tipo com o filtro da seção: todas as páginas de sections/mais-para-moto-tipos.liquid.
      async contar() {
        const pagina = async (numero, parametro = 'page') => {
          const url = new URL(this.base);
          url.searchParams.set('section_id', 'mais-para-moto-tipos');
          if (numero > 1) url.searchParams.set(parametro, numero);
          const script = (await baixar(url)).querySelector('script[type="application/json"]');
          return {
            paginas: Number(script.dataset.pages) || 1,
            parametro: script.dataset.pageParam || 'page',
            ignorado: script.dataset.filtrados === script.dataset.total,
            tipos: JSON.parse(script.textContent).tipos.filter(Boolean),
          };
        };
        const primeira = await pagina(1);
        // Com filtro de motos na URL, o mesmo total com e sem filtro é filtro ignorado (coleção com mais de 5.000).
        this.filtroIgnorado = this.base.searchParams.has(FILTRO_MOTOS) && primeira.ignorado;
        if (this.filtroIgnorado) return new Map();
        const resto = await Promise.all(
          Array.from({ length: Math.min(primeira.paginas, MAX_PAGINAS) - 1 }, (_, indice) =>
            pagina(indice + 2, primeira.parametro)
          )
        );
        const contagem = new Map();
        [primeira, ...resto]
          .flatMap((resultado) => resultado.tipos)
          .forEach((tipo) => contagem.set(tipo, (contagem.get(tipo) || 0) + 1));
        return contagem;
      }

      // URL da coleção da seção só com os tipos do departamento.
      urlDosTipos(tipos) {
        const url = new URL(this.base);
        url.searchParams.delete(FILTRO_TIPO);
        tipos.forEach((tipo) => url.searchParams.append(FILTRO_TIPO, tipo));
        return url;
      }

      // Cards do departamento: todas as páginas de sections/mais-para-moto-itens.liquid (até 4).
      async itens(departamento) {
        const url = this.urlDosTipos(departamento.categorias.map((categoria) => categoria.tipo));
        url.searchParams.set('sort_by', 'best-selling');
        url.searchParams.set('section_id', 'mais-para-moto-itens');
        const pagina = async (numero, parametro = 'page') => {
          const pedido = new URL(url);
          if (numero > 1) pedido.searchParams.set(parametro, numero);
          const lista = (await baixar(pedido)).querySelector('[data-mais-moto-itens]');
          return {
            paginas: Number(lista?.dataset.pages) || 1,
            parametro: lista?.dataset.pageParam || 'page',
            itens: lista ? [...lista.querySelectorAll(':scope > li[data-tipo]')] : [],
          };
        };
        const primeira = await pagina(1);
        const resto = await Promise.all(
          Array.from({ length: Math.min(primeira.paginas, 4) - 1 }, (_, indice) => pagina(indice + 2, primeira.parametro))
        );
        return [primeira, ...resto].flatMap((resultado) => resultado.itens);
      }

      // Produtos que já estão na página: o do produto e os cards dos semelhantes (id "CardLink-<seção>-<produto>").
      naPagina() {
        const ids = new Set([String(this.config.produto)]);
        document.querySelectorAll('.semelhantes--produto a[id^="CardLink-"]').forEach((link) => {
          if (!link.closest('mais-para-moto')) ids.add(link.id.slice(link.id.lastIndexOf('-') + 1));
        });
        return ids;
      }

      // Uma linha do departamento: até POR_LINHA cards, um de cada categoria por vez, na ordem das categorias.
      montarLinha(departamento, itens, fora) {
        const porTipo = new Map();
        itens.forEach((item) => {
          if (fora.has(item.dataset.produto)) return;
          if (!porTipo.has(item.dataset.tipo)) porTipo.set(item.dataset.tipo, []);
          porTipo.get(item.dataset.tipo).push(item);
        });
        const filas = departamento.categorias.map((categoria) => porTipo.get(categoria.tipo) || []);
        const escolhidos = [];
        while (escolhidos.length < POR_LINHA && filas.some((fila) => fila.length)) {
          filas.forEach((fila) => {
            if (fila.length && escolhidos.length < POR_LINHA) escolhidos.push(fila.shift());
          });
        }
        if (!escolhidos.length) return null;

        const chave = `${this.dataset.sectionId}-${++this.grupoSeq}`;
        const grupo = this.querySelector('template[data-grupo]').content.firstElementChild.cloneNode(true);
        const titulo = grupo.querySelector('.semelhantes__grupo-titulo');
        const fileira = grupo.querySelector('[data-fileira]');
        titulo.id = `MaisMotoGrupo-${chave}`;
        fileira.id = `MaisMotoLinha-${chave}`;
        fileira.setAttribute('aria-labelledby', titulo.id);
        escolhidos.forEach((item, indice) => {
          const novo = document.importNode(item, true);
          novo.id = `MaisMotoItem-${chave}-${indice + 1}`;
          fileira.append(novo);
        });
        const link = this.urlDosTipos(departamento.categorias.map((categoria) => categoria.tipo));
        grupo.querySelector('[data-link]').href = link.pathname + link.search;
        grupo.querySelector('[data-nome]').textContent = departamento.nome;
        grupo.querySelector('[data-qtd]').textContent = departamento.total;
        grupo.querySelector('[data-qtd-texto]').textContent = `, ${departamento.total} ${
          departamento.total === 1 ? 'produto' : 'produtos'
        }`;
        return grupo;
      }
    }
  );
}
